import React, {CSSProperties, MouseEvent, useRef, useState} from 'react'

import _ from 'underscore'

import Colors from '@utils/Colors.js'
import Popover, {PopoverRef} from '../overlays/Popover.js'
import ListItem from '../surfaces/ListItem.js'
import {buttonResetStyle} from '../buttons/styles.js'
import {focusableClass, ensureFocusStyles} from '../styles.js'
import {navActiveColor} from './styles.js'
import type {NavBarBranding, NavBarHistory, NavBarLinkItem} from './types.js'

ensureFocusStyles()

export interface NavBarLinkProps {
    /** The entry in the bar; with links only its icon and title are used */
    link?: NavBarLinkItem
    /** A menu under the entry; falsy entries are skipped and a single one is shown as the entry itself */
    links?: (NavBarLinkItem | false | null | undefined)[]
    /** Underlined with the branding colour */
    active?: boolean
    branding?: NavBarBranding
    history: NavBarHistory
}

// Whether a click on a link is a plain one, which navigates in the app: a modified or middle click opens a new tab
function isPlainClick(event: MouseEvent) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

/**
 * An entry of the NavBar: a link to link.target (navigates with history, a new tab with a middle or modified click),
 * a button that calls link.onClick, or with links a button that opens a menu of them. The active one is underlined.
 */
export default function NavBarLink({branding, active, history, ...props}: NavBarLinkProps) {
    const popoverRef = useRef<PopoverRef>(null)
    const [menuOpen, setMenuOpen] = useState(false)

    let {links, link} = props

    if (links) {
        links = _.filter(links, (link) => !!link)

        if (links.length === 1) {
            link = links[0] as NavBarLinkItem
            links = null
        }
    }

    const onNavigate = (target: string) => {
        history.push(target)
        popoverRef.current?.close()
    }

    const style: CSSProperties = {
        ...buttonResetStyle,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: 16,
        color: Colors.textDark,
        textDecoration: 'none',
        cursor: 'pointer',
        width: 'auto',
        height: '100%',
        marginLeft: 20
    }

    const underline = <div style={{height: 4, background: active ? navActiveColor(branding) : Colors.backgroundWhite, width: '100%', transition: 'all ease 0.1s'}} />

    if (links) {
        return (
            <Popover
                ref={popoverRef}
                popoverStyle={{marginLeft: 6}}
                onToggle={setMenuOpen}
                content={
                    <>
                        {links.map((link) => {
                            if (link) {
                                return (
                                    <ListItem key={link.title} active={link.active} onClick={link.onClick || (() => onNavigate(link.target))}>
                                        {link.icon && <i style={{marginRight: 12, fontSize: 20}} className={link.icon} />}
                                        {link.title}
                                    </ListItem>
                                )
                            }
                        })}
                    </>
                }
            >
                {/* The Popover's wrapper opens the menu on the click */}
                <button type='button' className={focusableClass} style={style} aria-haspopup='menu' aria-expanded={menuOpen}>
                    <div style={{height: '100%', display: 'flex', alignItems: 'center'}}>
                        {link.icon && <i aria-hidden style={{marginRight: 12, fontSize: 20}} className={link.icon} />}
                        {link.title}
                        <i aria-hidden className='mdi mdi-menu-down' />
                    </div>

                    {underline}
                </button>
            </Popover>
        )
    }

    const content = (
        <>
            <div style={{height: '100%', display: 'flex', alignItems: 'center'}}>
                {link.icon && <i aria-hidden style={{marginRight: link.title ? 10 : 0, fontSize: 20}} className={link.icon} />}
                {link.title}
            </div>

            {underline}
        </>
    )

    if (link.onClick) {
        return (
            <button type='button' className={focusableClass} style={style} aria-label={link.title ? undefined : link.label} onClick={link.onClick}>
                {content}
            </button>
        )
    }

    return (
        <a
            className={focusableClass}
            style={style}
            href={link.target}
            aria-label={link.title ? undefined : link.label}
            aria-current={active ? 'page' : undefined}
            onClick={(event) => {
                if (isPlainClick(event)) {
                    event.preventDefault()
                    onNavigate(link.target)
                }
            }}
        >
            {content}
        </a>
    )
}
